import { jsPDF } from 'jspdf';
import { sanitizeDocumentHtml } from './sanitizeDocumentHtml';
import { applyDocxLayoutFixes } from './docxLayoutFix';
import { renderDocxHostToPdf } from './docxPdfRender';

// Renders an HR-authored, server-filled HTML document (Latin + Arabic/RTL, inline base64
// images) to a multi-page A4 PDF in the browser and triggers a download.
//
// WHY CLIENT-SIDE: the browser's own layout engine shapes Arabic, runs the bidi algorithm, and
// wraps lines correctly. Free server-side engines can't do this on Windows (iText needs the paid
// pdfCalligraph; Chromium/wkhtmltopdf are blocked by the Azure Windows App Service sandbox). So
// the backend fills the template with the caller's own data and returns HTML; we render it here.
//
// html2canvas screenshots the already-shaped DOM, so Arabic comes out correct. The output is a
// raster image embedded in the PDF (visually faithful, but text is not selectable/searchable).
//
// SECURITY: the HTML is HR-authored and injected into the DOM to render, so it must be sanitized
// (DOMPurify) to neutralize stored XSS before it touches the page.

const A4_CONTENT_WIDTH_PX = 794; // 210mm at 96dpi — also the exact native-mode `section.docx` width

export async function downloadHtmlAsPdf(filledHtml: string, fileName: string): Promise<void> {
  // Code-split html2canvas (~48 KB gz) so it only loads when a document is actually downloaded.
  const { default: html2canvas } = await import('html2canvas');

  // Strip <script>/on*-handlers while keeping docx-preview's scoped <style> block and the
  // class/style/table attributes the high-fidelity render relies on (see sanitizeDocumentHtml).
  const cleanHtml = sanitizeDocumentHtml(filledHtml);

  // Off-screen host at the A4 content width (== the native `section.docx` width, so the page box
  // fits exactly; and == the legacy fallback width, so non-docx HTML — e.g. the settlement letter —
  // renders identically to before). dir="auto" lets each block pick its own base direction.
  const host = document.createElement('div');
  host.setAttribute('dir', 'auto');
  Object.assign(host.style, {
    position: 'fixed',
    top: '0',
    insetInlineStart: '-10000px',
    width: `${A4_CONTENT_WIDTH_PX}px`,
    background: '#ffffff',
    color: '#000000',
    padding: '0',
  } as Partial<CSSStyleDeclaration>);
  host.innerHTML = cleanHtml;
  document.body.appendChild(host);

  try {
    // Arabic shaping breaks if a webfont isn't loaded before capture.
    if (document.fonts?.ready) {
      try { await document.fonts.ready; } catch { /* fonts API best-effort */ }
    }

    // Center full-bleed header/footer bars + behind-text watermarks in the real render context so
    // the PDF matches the wizard preview and Word (see utils/docxLayoutFix.ts).
    applyDocxLayoutFixes(host);
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

    // NATIVE PAGE MODE (current converter): one `section.docx` A4 box per page — screenshot each into
    // its own A4 PDF page so header/footer/watermark land exactly where Word puts them. A single-page
    // section renders whole; a plain over-tall section (un-paginated overflow) is sliced across pages;
    // an over-tall section carrying a header/footer/watermark re-stamps those bands on every page (Word
    // repeats them). Non-docx HTML (the settlement letter) falls back to one tall sliced canvas. See
    // utils/docxPdfRender.ts.
    await renderDocxHostToPdf(pdf, host, html2canvas);

    pdf.save(fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`);
  } finally {
    document.body.removeChild(host);
  }
}
